import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { ambassadorKindFor } from "@majourneys/shared";
import { useAmbassadorMe, useAmbassadorMutations } from "../hooks/use-ambassador";
import { CONTROL_EDGE, GOLD, GOLD_DARK, NAVY, WHITE_FACE, WHITE_INSET } from "./kit";

export interface FavouritableListing {
  id: string;
  slug: string;
  mode?: "bookable" | "explore" | null;
  template?: string | null;
  segment?: string | null;
  category?: { slug: string } | null;
}

/**
 * Can this listing go on an ambassador page? Bookable, and inside the
 * program: never a stay, a camp or a transfer (same rule the API uses).
 */
export function isFavouritable(a: FavouritableListing): boolean {
  if (a.mode === "explore") return false;
  return (
    ambassadorKindFor({
      template: a.template ?? null,
      segment: a.segment ?? null,
      categorySlug: a.category?.slug ?? null,
      activitySlug: a.slug,
      isTransfer: a.template === "transfer",
    }) !== null
  );
}

/**
 * "Add to my page" on a listing (Task 15). Only active ambassadors see it,
 * only on listings in the program. Everyone else gets nothing, so the
 * cached public HTML never changes: `me` is read in the browser.
 */
export function AmbassadorFavouriteButton({ activity }: { activity: FavouritableListing }) {
  const { t } = useTranslation();
  const me = useAmbassadorMe();
  const { favourite } = useAmbassadorMutations();
  const a = me.data?.ambassador;
  const saved = Boolean(a?.favouriteIds.includes(activity.id));
  // Shows the tap at once; `me` catches up after the request.
  const [on, setOn] = useState(saved);
  useEffect(() => setOn(saved), [saved]);

  if (!a || a.status !== "active" || !isFavouritable(activity)) return null;

  return (
    <div className="mt-3 flex">
      <button
        type="button"
        aria-pressed={on}
        disabled={favourite.isPending}
        onClick={() => {
          const next = !on;
          setOn(next);
          favourite.mutate({ activityId: activity.id, on: next }, { onError: () => setOn(!next) });
        }}
        className="mj-press inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-4 font-sans text-[13px] font-semibold"
        style={{ color: NAVY, background: WHITE_FACE, boxShadow: `${WHITE_INSET}, 0 0 0 1.5px ${CONTROL_EDGE}` }}
      >
        <Icon
          key={on ? "on" : "off"}
          name={on ? "ri-heart-fill" : "ri-heart-line"}
          className={`text-[15px] ${on ? "mj-pop-in" : ""}`}
          style={{ color: on ? GOLD : NAVY }}
          aria-hidden
        />
        <span style={{ color: on ? GOLD_DARK : NAVY }}>
          {on ? t("ambassadors.favourite.on") : t("ambassadors.favourite.off")}
        </span>
      </button>
    </div>
  );
}
